import React, { useEffect, useMemo, useState } from "react";
import Paper from "@mui/material/Paper";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { surfaceSx } from "./channelTheme";
import { ChannelTile, ZoneTile } from "./ChannelTile";
import ChannelTable from "./ChannelTable";
import GatewayChannelSummary from "./GatewayChannelSummary";
import { CHANNEL_GROUPS, GROUP_ORDER, filterChannels, groupChannels, needsAttention } from "./channelUtils";

const VIEW_KEY = "gw-channels:view";
const VIEWS = [
  { key: "cards", icon: "grid_view", label: "Cards" },
  { key: "compact", icon: "view_module", label: "Compact" },
  { key: "table", icon: "table_rows", label: "Table" },
];

const readView = () => {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return VIEWS.some((x) => x.key === v) ? v : "cards";
  } catch (e) {
    return "cards";
  }
};

export function SectionHeader({ title, subtitle, action }) {
  return (
    <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
      <MDBox>
        <MDTypography variant="h6" fontWeight="bold">
          {title}
        </MDTypography>
        {subtitle && (
          <MDTypography variant="caption" color="text">
            {subtitle}
          </MDTypography>
        )}
      </MDBox>
      {action}
    </MDBox>
  );
}

export default function ChannelOverview({
  id,
  channels = [],
  selectedKey,
  onSelect,
  filter: controlledFilter,
  onFilterChange,
}) {
  const [innerFilter, setInnerFilter] = useState("all");
  const filter = controlledFilter ?? innerFilter;
  const setFilter = (f) => (onFilterChange ? onFilterChange(f) : setInnerFilter(f));
  const [query, setQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [view, setView] = useState(readView);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch (e) {
      /* storage unavailable */
    }
  }, [view]);

  const presentGroups = useMemo(
    () => GROUP_ORDER.filter((g) => channels.some((c) => c.group === g)),
    [channels]
  );
  const attentionCount = useMemo(() => channels.filter(needsAttention).length, [channels]);

  // Drop a group filter if that group disappears (e.g. live update), or attention when nothing needs it.
  useEffect(() => {
    if (filter === "attention" && attentionCount === 0) setFilter("all");
    else if (filter !== "all" && filter !== "attention" && !presentGroups.includes(filter)) setFilter("all");
  }, [filter, attentionCount, presentGroups]);

  const visible = useMemo(() => filterChannels(channels, { filter, query }), [channels, filter, query]);
  const groups = useMemo(() => groupChannels(visible), [visible]);
  const minTile = view === "compact" ? 170 : 250;

  const filterButton = (key, label, color = "info") => (
    <MDButton
      key={key}
      size="small"
      color={color}
      variant={filter === key ? "gradient" : "outlined"}
      onClick={() => setFilter(key)}
      aria-pressed={filter === key}
      sx={{ minHeight: 30, py: 0.5, px: 1.5 }}
    >
      {label}
    </MDButton>
  );

  return (
    <Paper id={id} elevation={0} sx={(theme) => ({ ...surfaceSx(theme), p: { xs: 2, md: 2.5 }, mb: 3 })}>
      <SectionHeader
        title="Channels"
        subtitle="All gateway channels, grouped by type. Select a channel to see its detail and history."
      />

      <MDBox mt={2}>
        <GatewayChannelSummary channels={channels} filter={filter} onFilterChange={setFilter} />
      </MDBox>

      <MDBox mt={2.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <MDBox display="flex" gap={1} flexWrap="wrap" flex="1 1 auto">
          {filterButton("all", `All (${channels.length})`)}
          {presentGroups.length > 1 &&
            presentGroups.map((g) =>
              filterButton(g, `${CHANNEL_GROUPS[g].label} (${channels.filter((c) => c.group === g).length})`)
            )}
          {attentionCount > 0 && filterButton("attention", `Needs attention (${attentionCount})`, "error")}
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDInput
            size="small"
            placeholder="Search channels"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputProps={{ "aria-label": "Search channels" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon fontSize="small">search</Icon>
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />
          <MDBox display="flex" role="group" aria-label="View">
            {VIEWS.map((v) => (
              <Tooltip key={v.key} title={v.label}>
                <IconButton
                  size="small"
                  onClick={() => setView(v.key)}
                  aria-pressed={view === v.key}
                  aria-label={`${v.label} view`}
                  sx={({ palette, functions: { rgba } }) => ({
                    color: view === v.key ? palette.info.main : palette.text.main,
                    backgroundColor: view === v.key ? rgba(palette.info.main, 0.14) : "transparent",
                  })}
                >
                  <Icon>{v.icon}</Icon>
                </IconButton>
              </Tooltip>
            ))}
          </MDBox>
        </MDBox>
      </MDBox>

      <MDBox mt={2}>
        {visible.length === 0 ? (
          <MDBox py={4} textAlign="center">
            <MDTypography variant="button" color="text" display="block" mb={1}>
              {channels.length ? "No channels match the current filter." : "This gateway has no channels yet."}
            </MDTypography>
            {channels.length > 0 && (
              <MDButton
                size="small"
                color="info"
                variant="outlined"
                onClick={() => {
                  setFilter("all");
                  setQuery("");
                }}
              >
                Show all
              </MDButton>
            )}
          </MDBox>
        ) : view === "table" ? (
          <ChannelTable channels={visible} selectedKey={selectedKey} onSelect={onSelect} />
        ) : (
          groups.map((g) => (
            <MDBox key={g.key} mb={2}>
              <MDBox
                component="button"
                type="button"
                aria-expanded={!collapsedGroups[g.key]}
                aria-controls={`gateway-group-${g.key}`}
                onClick={() => setCollapsedGroups((previous) => ({
                  ...previous,
                  [g.key]: !previous[g.key],
                }))}
                display="flex"
                alignItems="center"
                gap={1}
                mb={1}
                sx={(theme) => ({
                  width: "100%",
                  background: "transparent",
                  border: 0,
                  py: 1,
                  px: 0,
                  cursor: "pointer",
                  textAlign: "left",
                  color: theme.palette.text.main,
                  "&:focus-visible": { outline: `2px solid ${theme.palette.info.main}`, outlineOffset: 2 },
                })}
              >
                <Icon className={g.icon} sx={({ palette }) => ({ fontSize: "0.8rem !important", color: palette.text.main })} />
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
                >
                  {g.label} · {g.count}
                </MDTypography>
                <Icon sx={{ ml: "auto" }}>
                  {collapsedGroups[g.key] ? "expand_more" : "expand_less"}
                </Icon>
              </MDBox>
              <MDBox
                id={`gateway-group-${g.key}`}
                hidden={!!collapsedGroups[g.key]}
                display={collapsedGroups[g.key] ? "none" : "grid"}
                gap={1.5}
                gridTemplateColumns={`repeat(auto-fill, minmax(${minTile}px, 1fr))`}
              >
                {g.items.map((item) =>
                  item.type === "zone" ? (
                    <ZoneTile
                      key={item.key}
                      zone={item}
                      selectedKey={selectedKey}
                      density={view}
                      onSelect={onSelect}
                    />
                  ) : (
                    <ChannelTile
                      key={item.channel.key}
                      channel={item.channel}
                      selected={String(selectedKey) === item.channel.key}
                      density={view}
                      onSelect={onSelect}
                    />
                  )
                )}
              </MDBox>
            </MDBox>
          ))
        )}
      </MDBox>
    </Paper>
  );
}
